import { useState, useEffect, useCallback } from "react";
import api, { fmtMoney, fmtDate, errDetail, PAYMENT_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { PageHeader } from "../components/CrudPage";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Eye, Undo2, Search } from "lucide-react";
import { toast } from "sonner";

export default function SalesHistory() {
  const { hasPerm } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({ payment_method: "", status: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [refundRow, setRefundRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/sales", { params: { page, search, limit: 15, ...filters } });
      setData(d);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setLoading(false); }
  }, [page, search, filters]);

  useEffect(() => { load(); }, [load]);

  const doRefund = async () => {
    try {
      await api.post(`/sales/${refundRow.id}/refund`);
      toast.success("İade tamamlandı, stok ve kasa güncellendi");
      setRefundRow(null);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const columns = [
    { key: "sale_no", label: "Satış No", render: (r) => <span className="font-semibold">{r.sale_no}</span> },
    { key: "created_at", label: "Tarih / Saat", render: (r) => fmtDate(r.created_at) },
    { key: "user_name", label: "Kullanıcı" },
    { key: "items", label: "Ürünler", render: (r) => <span className="text-gray-500">{r.items.length} kalem</span> },
    { key: "total", label: "Toplam", render: (r) => <span className="font-semibold">{fmtMoney(r.total)}</span> },
    { key: "payment_method", label: "Ödeme", render: (r) => PAYMENT_LABELS[r.payment_method] },
    { key: "gross_profit", label: "Brüt Kar", render: (r) => fmtMoney(r.gross_profit) },
    { key: "net_profit", label: "Net Kar", render: (r) => fmtMoney(r.net_profit) },
    {
      key: "status", label: "Durum",
      render: (r) => (
        <span className={`px-2 py-1 text-xs font-semibold ${r.status === "iade" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>
          {r.status === "iade" ? "İade Edildi" : "Tamamlandı"}
        </span>
      ),
    },
  ];

  return (
    <div data-testid="sales-history-page">
      <PageHeader title="Satış Geçmişi" subtitle="Tüm satış ve iade kayıtları" />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchInput); }} className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="sales-search-input" className="w-56 rounded-none pl-9" placeholder="Satış no, ürün, kullanıcı..."
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
          <Button type="submit" variant="outline" className="rounded-none">Ara</Button>
        </form>
        <Input data-testid="sales-date-from" type="date" className="w-40 rounded-none" value={filters.date_from}
          onChange={(e) => { setPage(1); setFilters({ ...filters, date_from: e.target.value }); }} />
        <Input data-testid="sales-date-to" type="date" className="w-40 rounded-none" value={filters.date_to}
          onChange={(e) => { setPage(1); setFilters({ ...filters, date_to: e.target.value }); }} />
        <Select value={filters.payment_method || "all"}
          onValueChange={(v) => { setPage(1); setFilters({ ...filters, payment_method: v === "all" ? "" : v }); }}>
          <SelectTrigger data-testid="sales-payment-filter" className="w-40 rounded-none"><SelectValue placeholder="Ödeme Türü" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Ödeme: Tümü</SelectItem>
            {Object.entries(PAYMENT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status || "all"}
          onValueChange={(v) => { setPage(1); setFilters({ ...filters, status: v === "all" ? "" : v }); }}>
          <SelectTrigger data-testid="sales-status-filter" className="w-40 rounded-none"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Durum: Tümü</SelectItem>
            <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
            <SelectItem value="iade">İade Edildi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable testId="sales-table" columns={columns} data={data.items} loading={loading}
        page={page} pages={data.pages} total={data.total} onPage={setPage}
        actions={(row) => (
          <>
            <button data-testid={`sale-detail-${row.id}`} onClick={() => setDetail(row)}
              className="flex h-8 w-8 items-center justify-center border border-gray-200 text-[#0B1F3A] hover:border-[#D4AF37]">
              <Eye size={14} />
            </button>
            {hasPerm("sales_history", "delete") && row.status !== "iade" && (
              <button data-testid={`sale-refund-${row.id}`} onClick={() => setRefundRow(row)}
                className="flex h-8 w-8 items-center justify-center border border-gray-200 text-red-600 hover:border-red-400">
                <Undo2 size={14} />
              </button>
            )}
          </>
        )} />

      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Satış Detayı · {detail?.sale_no}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2 text-gray-600">
                <p>Tarih: <span className="text-[#0B1F3A]">{fmtDate(detail.created_at)}</span></p>
                <p>Kullanıcı: <span className="text-[#0B1F3A]">{detail.user_name}</span></p>
                <p>Ödeme: <span className="text-[#0B1F3A]">{PAYMENT_LABELS[detail.payment_method]}</span></p>
                <p>Müşteri: <span className="text-[#0B1F3A]">{detail.customer_name || "-"}</span></p>
              </div>
              <table className="w-full border border-gray-200 text-sm">
                <thead><tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <th className="px-3 py-2">Ürün</th><th className="px-3 py-2">Adet</th>
                  <th className="px-3 py-2">Fiyat</th><th className="px-3 py-2 text-right">Tutar</th></tr></thead>
                <tbody>
                  {detail.items.map((i, idx) => (
                    <tr key={idx} className="border-t border-gray-100">
                      <td className="px-3 py-2">{i.name}</td><td className="px-3 py-2">{i.quantity}</td>
                      <td className="px-3 py-2">{fmtMoney(i.price)}</td>
                      <td className="px-3 py-2 text-right">{fmtMoney(i.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="space-y-1 text-right">
                <p className="text-gray-500">Ara Toplam: {fmtMoney(detail.subtotal)}</p>
                {detail.discount_amount > 0 && <p className="text-red-500">İndirim: -{fmtMoney(detail.discount_amount)}</p>}
                <p className="font-heading text-lg font-semibold">Toplam: <span className="text-[#D4AF37]">{fmtMoney(detail.total)}</span></p>
                <p className="text-xs text-gray-400">Brüt Kar: {fmtMoney(detail.gross_profit)} · Net Kar: {fmtMoney(detail.net_profit)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(refundRow)} onOpenChange={(o) => !o && setRefundRow(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Satışı iade etmek istediğinize emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              Stoklar geri artacak, kasa güncellenecek ve kar hesapları düzeltilecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-refund-button" onClick={doRefund} className="rounded-none bg-red-600 hover:bg-red-700">
              İade Et
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
